/**
 * Sidebar panel for curve object properties — bevel depth, resolution,
 * fill caps, and cyclic toggle.
 *
 * Visible when a curve object is selected and NOT in edit mode.
 */

import { Box } from "lucide-react";
import { useMemo } from "react";
import { useStore } from "../../app/store.ts";
import {
  curveControlDiagonal,
  FALLBACK_DIAGONAL,
} from "../../modeling/objectBounds.ts";
import { useDeferredUndo } from "../hooks/useDeferredUndo.ts";
import { SidebarCard } from "../SidebarCard.tsx";
import { beginSceneObjectNameEyedropper } from "../sceneObjectEyedropper.ts";
import {
  CompactRow,
  EyeToggle,
  SectionLabel,
  SelectSearch,
  SidebarButton,
  SliderRow,
  Toggle,
} from "../shared.tsx";

export function CurvePropertiesPanel({ objectId }: { objectId: string }) {
  const obj = useStore((s) => s.sceneObjects.get(objectId));
  const sceneObjects = useStore((s) => s.sceneObjects);
  const updateCurveData = useStore((s) => s.updateCurveData);
  const pushUndo = useStore((s) => s.pushUndo);
  const undo = useDeferredUndo();

  const taperOptions = useMemo(
    () =>
      Array.from(sceneObjects.values())
        .filter((o) => o.id !== objectId && o.curveData != null)
        .map((o) => ({ value: o.name, label: o.name })),
    [sceneObjects, objectId],
  );

  if (!obj?.curveData) return null;
  const { bevel, resolutionU, cyclic, taperObjectName } = obj.curveData;
  // Use control-point extent (not the bevel-inflated SceneObject bbox) so
  // dragging Depth doesn't chase its own slider max.
  const diag = obj.curveData
    ? curveControlDiagonal(obj.curveData, obj.scale)
    : FALLBACK_DIAGONAL;
  // Slider thumb is a scaled integer (depth × 100), so the max multiplier
  // × 100 × diag stays an integer-ish scale the Slider can step through.
  const depthMax = Math.max(5, Math.round(0.25 * diag * 100));

  return (
    <SidebarCard style={{ overflow: "hidden" }}>
      <SectionLabel>Curve</SectionLabel>

      <Toggle
        label="Bevel"
        checked={bevel.enabled}
        onChange={(v) => {
          pushUndo();
          updateCurveData(objectId, { bevel: { ...bevel, enabled: v } });
        }}
      />

      {bevel.enabled && (
        <>
          <SliderRow
            density="compact"
            label="Depth"
            value={Math.round(bevel.depth * 100)}
            min={0}
            max={depthMax}
            displayValue={bevel.depth.toFixed(2)}
            parseValue={(raw) => parseFloat(raw) * 100}
            onStart={undo.pushOnce}
            onEnd={undo.reset}
            onChange={(v) =>
              updateCurveData(objectId, {
                bevel: { ...bevel, depth: v / 100 },
              })
            }
            dimChromeOnDrag
          />

          <SliderRow
            density="compact"
            label="Bevel Res"
            value={bevel.resolution}
            min={3}
            max={32}
            displayValue={String(bevel.resolution)}
            onStart={undo.pushOnce}
            onEnd={undo.reset}
            onChange={(v) =>
              updateCurveData(objectId, {
                bevel: { ...bevel, resolution: v },
              })
            }
            dimChromeOnDrag
          />

          <EyeToggle
            label="Fill Caps"
            checked={bevel.fillCaps}
            onChange={(v) => {
              pushUndo();
              updateCurveData(objectId, { bevel: { ...bevel, fillCaps: v } });
            }}
          />
        </>
      )}

      <SliderRow
        density="compact"
        label="Curve Res"
        value={resolutionU}
        min={1}
        max={64}
        displayValue={String(resolutionU)}
        onStart={undo.pushOnce}
        onEnd={undo.reset}
        onChange={(v) => updateCurveData(objectId, { resolutionU: v })}
        dimChromeOnDrag
      />

      <EyeToggle
        label="Closed"
        checked={cyclic}
        onChange={(v) => {
          pushUndo();
          updateCurveData(objectId, { cyclic: v });
        }}
      />

      <CompactRow label="Taper Object">
        <SelectSearch
          value={taperObjectName ?? null}
          options={taperOptions}
          placeholder="None"
          onChange={(name) => {
            pushUndo();
            updateCurveData(objectId, { taperObjectName: name ?? null });
          }}
          eyedropperPick={(onPick, onCancel) => {
            beginSceneObjectNameEyedropper(
              onPick,
              onCancel,
              (candidate) =>
                candidate.id !== objectId && candidate.curveData != null,
            );
          }}
        />
      </CompactRow>

      <div style={{ marginTop: 8 }}>
        <SidebarButton
          icon={<Box size={13} />}
          label="Convert to Mesh"
          active={false}
          onClick={() => {
            pushUndo();
            useStore.getState().convertCurveToMesh(objectId);
          }}
        />
      </div>
    </SidebarCard>
  );
}
